import { ActionDefinition } from "~/types/actions";
import { CharacterData } from "~/types/storage";
import { getTableActions } from "~/pages/shared/tableActions";


/**
 * Actions specifically for the Strain Tracker when it has focus.
 */
export const STRAIN_ACTION_INCREASE: ActionDefinition =
{
  id: "strain-increase",
  label: "Increase Strain",
  description: "Add 1 Strain (when focused: ↑, +, or =)",
  keywords: ["strain", "increase", "add", "up"],
  shortcut: ["+", "=", "ArrowUp"],
  category: "strain",
}

export const STRAIN_ACTION_DECREASE: ActionDefinition =
{
  id: "strain-decrease",
  label: "Decrease Strain",
  description: "Remove 1 Strain (when focused: ↓, -, or _)",
  keywords: ["strain", "decrease", "remove", "down"],
  shortcut: ["-", "_", "ArrowDown"],
  category: "strain",
}

export const getDerivedStatActions = (label: string): ActionDefinition[] => [
  {
    id: `increase-${label.toLowerCase()}`,
    label: `Increase ${label}`,
    description: `Increase ${label} by 1`,
    keywords: ["increase", "add", label.toLowerCase()],
    shortcut: ["+", "=", "ArrowUp"],
    category: label.toLowerCase(),
  },
  {
    id: `decrease-${label.toLowerCase()}`,
    label: `Decrease ${label}`,
    description: `Decrease ${label} by 1`,
    keywords: ["decrease", "remove", label.toLowerCase()],
    shortcut: ["-", "_", "ArrowDown"],
    category: label.toLowerCase(),
  },
]

/**
 * Registry of all character actions
 *
 * These actions power both the command palette and existing UI buttons.
 * Each action has a unique ID and display information.
 */
export const getCharacterActions = (character: CharacterData): ActionDefinition[] => [
  // Combat actions
  {
    id: "damage",
    label: `Damage`,
    description: "Take damage (absorbed by Resilience first)",
    keywords: ["damage", "hit", "hurt", "take"],
    shortcut: "D",
    category: "combat",
  },
  {
    id: "heal",
    label: `Heal (${character.name})`,
    description: "Restore HP",
    keywords: ["heal", "health", "restore", "hp", "recover"],
    shortcut: "H",
    category: "combat",
  },
  {
    id: "direct-damage",
    label: "Direct Damage " + character.name,
    description: "Take damage directly to HP (bypasses Resilience)",
    keywords: ["direct", "damage", "bypass", "resilience", "true"],
    shortcut: "X",
    category: "combat",
  },
  {
    id: "spend-resilience",
    label: "Spend Resilience " + character.name,
    description: "Spend Resilience points",
    keywords: ["spend", "resilience", "res", "aura"],
    shortcut: "V",
    category: "combat",
  },
  {
    id: "end-combat",
    label: "End Combat",
    description: "Apply end of combat strain damage",
    keywords: ["end", "combat", "strain", "reset", "finish"],
    category: "combat",
  },

  // Strain focus actions (when strain tracker is focused)
  {
    id: "focus-strain",
    label: "Focus Strain Tracker",
    description: "Focus on the Strain widget (when focused: ↑↓ to adjust)",
    keywords: ["strain", "focus", "tracker"],
    shortcut: "S",
    category: "focus",
  },

  // Rest actions
  {
    id: "snack-break",
    label: "Snack Break",
    description: "Recover 2 Resilience and remove 1 Strain (~15 min)",
    keywords: ["snack", "break", "rest", "short", "recover", "resilience"],
    category: "rest",
  },
  {
    id: "meal-break",
    label: "Meal Break",
    description: "Recover all Resilience and remove all Strain (~1 hour)",
    keywords: ["meal", "break", "rest", "lunch", "dinner", "recover"],
    category: "rest",
  },
  {
    id: "full-rest",
    label: "Good Night's Rest",
    description: "Full recovery: Resilience, Strain, roll to heal HP (~6-8 hours)",
    keywords: ["rest", "sleep", "night", "full", "heal", "recover", "hp"],
    category: "rest",
  },

  // Navigation actions
  {
    id: "open-roll-search",
    label: "Roll",
    description: "Open the roll search to make a dice roll",
    keywords: ["roll", "dice", "skill", "check"],
    shortcut: "R",
    //category: "navigation",
  },
  {
    id: "open-character-search",
    label: "Switch Character",
    description: "Open character switcher",
    keywords: ["switch", "character", "change", "select"],
    shortcut: "C",
    category: "navigation",
  },

  // Table actions (shared with GmView)
  ...getTableActions(),

  // Character management actions
  {
    id: "new-character",
    label: "New Character",
    description: "Create a new character with default values",
    keywords: ["new", "create", "character", "add"],
    category: "navigation",
  },
  {
    id: "delete-character",
    label: "Delete Character",
    description: "Delete the current character",
    keywords: ["delete", "remove", "character"],
    category: "navigation",
  },
];
